import React, { useEffect, useMemo, useState } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import Breadcrumbs from "~/components/Breadcrumbs";
import FiltersBar from "~/components/FiltersBar";
import PageHeader from "~/components/PageHeader";
import { getCurrentPage } from "~/functions";
import api from "~/services/api";
import DSODashboardDetailsModal from "./DashboardDetailsModal";

const CARD_CLASS =
  "rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)]";

const formatNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value)
    ? value.toLocaleString("pt-BR")
    : "—";

const formatPercent = (value) =>
  typeof value === "number" && !Number.isNaN(value) ? `${value}%` : "—";

const SmallStatCard = ({ label, value, color }) => (
  <div className="flex flex-col items-center px-4">
    <span className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">{label}</span>
    <span className={`text-2xl font-bold ${color || "text-gray-800"}`}>{value}</span>
  </div>
);

const formatDate = (date) => {
  const safe = new Date(date);
  safe.setHours(12, 0, 0, 0);
  return safe.toISOString().slice(0, 10);
};

const buildPieData = (summary) => {
  const approved = summary?.approved ?? 0;
  const denied = summary?.denied ?? 0;
  const processing = summary?.processing ?? 0;
  const total = summary?.total ?? 0;
  const pending = Math.max(0, total - approved - denied - processing);
  return [
    { name: "Approved", value: approved },
    { name: "Denied", value: denied },
    ...(processing > 0 ? [{ name: "Processing", value: processing }] : []),
    ...(pending > 0 ? [{ name: "Pending", value: pending }] : []),
  ];
};

const normalizeTypeLabel = (type) => (type === "Initial" ? "Initial Visa" : type || "—");

const collectTypes = (...sections) => {
  const seen = new Set();
  const list = [];
  sections.forEach((section) => {
    (section?.rows || []).forEach((row) => {
      if (!row?.type || seen.has(row.type)) return;
      seen.add(row.type);
      list.push(row.type);
    });
  });
  return list;
};

const mapRowsByType = (section) => {
  const map = new Map();
  (section?.rows || []).forEach((row) => {
    map.set(row.type, row);
  });
  return map;
};

export default function DSODashboard() {
  const currentPage = getCurrentPage();
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return formatDate(firstDay);
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return formatDate(lastDay);
  });
  const [selectedCampus, setSelectedCampus] = useState("all");
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [dashboardData, setDashboardData] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsContext, setDetailsContext] = useState(null);
  const [filials, setFilials] = useState([]);
  const [agents, setAgents] = useState([]);

  const columnGroups = [
    {
      title: "Onboarding Pendings",
      section: "onboarding",
      columns: [
        { label: "Pending", key: "onboardingPending", status: "pending" },
        { label: "Form Check", key: "onboardingFormCheck", status: "form_check" },
        { label: "Concluded", key: "onboardingConcluded", status: "concluded" },
      ],
    },
    {
      title: "Pre Arrivals",
      section: "pre_arrivals",
      columns: [
        { label: "Processing", key: "preArrivalsProcessing", status: "processing" },
        { label: "PT Pending", key: "preArrivalsPtPending", status: "pt_pending" },
        { label: "Denied", key: "preArrivalsDenied", status: "denied" },
        { label: "Gave-up", key: "preArrivalsGaveUp", status: "gave_up" },
        { label: "Approved", key: "preArrivalsApproved", status: "approved" },
      ],
    },
    {
      title: "Arrivals",
      section: "arrivals",
      columns: [
        { label: "PT Pending", key: "arrivalsPtPending", status: "pt_pending" },
        { label: "Concluded", key: "arrivalsConcluded", status: "concluded" },
      ],
    },
    {
      title: "Orientation",
      section: "orientation",
      columns: [{ label: "Total", key: "orientationTotal" }],
    },
  ];

  const allColumns = columnGroups.flatMap((group) =>
    group.columns.map((col) => ({
      ...col,
      group: group.title,
      section: group.section,
    }))
  );

  const fetchFilials = async () => {
    try {
      const response = await api.get("/filials");
      const rows = response.data?.rows || [];
      setFilials(rows);
    } catch (error) {
      setFilials([]);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get("/commercial-dashboard/agents-simple");
      setAgents(response.data?.rows || []);
    } catch (error) {
      setAgents([]);
    }
  };

  const buildDashboardParams = () => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (selectedAgent !== "all") params.agent_id = selectedAgent;
    if (selectedCampus !== "all") params.filial_id = selectedCampus;
    return Object.keys(params).length ? params : undefined;
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get("/dso-dashboard", {
        params: buildDashboardParams(),
      });
      setDashboardData(response.data);
    } catch (error) {
      setDashboardData(null);
    }
  };

  useEffect(() => {
    fetchFilials();
    fetchAgents();
    fetchDashboard();
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [startDate, endDate, selectedAgent, selectedCampus]);

  const tableRows = useMemo(() => {
    if (!dashboardData) return [];
    const onboardingMap = mapRowsByType(dashboardData.onboarding_pendings);
    const preArrivalsMap = mapRowsByType(dashboardData.pre_arrivals);
    const arrivalsMap = mapRowsByType(dashboardData.arrivals);
    const orientationMap = mapRowsByType(dashboardData.orientation);
    const types = collectTypes(
      dashboardData.onboarding_pendings,
      dashboardData.pre_arrivals,
      dashboardData.arrivals,
      dashboardData.orientation
    );

    return types.map((type) => {
      const onboardingRow = onboardingMap.get(type) || {};
      const preArrivalsRow = preArrivalsMap.get(type) || {};
      const arrivalsRow = arrivalsMap.get(type) || {};
      const orientationRow = orientationMap.get(type) || {};

      return {
        rawType: type,
        type: normalizeTypeLabel(type),
        onboardingPending: onboardingRow.pending ?? 0,
        onboardingFormCheck: onboardingRow.form_check ?? 0,
        onboardingConcluded: onboardingRow.concluded ?? 0,
        preArrivalsProcessing: preArrivalsRow.processing ?? 0,
        preArrivalsPtPending: preArrivalsRow.pt_pending ?? 0,
        preArrivalsDenied: preArrivalsRow.denied ?? 0,
        preArrivalsGaveUp: preArrivalsRow.gave_up ?? 0,
        preArrivalsApproved: preArrivalsRow.approved ?? 0,
        arrivalsPtPending: arrivalsRow.pt_pending ?? 0,
        arrivalsConcluded: arrivalsRow.concluded ?? 0,
        orientationTotal: orientationRow.total ?? 0,
      };
    });
  }, [dashboardData]);

  const visaInterviewsData = useMemo(
    () => dashboardData?.total_visa_interviews || null,
    [dashboardData]
  );

  const cosData = useMemo(
    () => dashboardData?.total_cos || null,
    [dashboardData]
  );

  const pieDataVisa = useMemo(
    () => buildPieData(visaInterviewsData),
    [visaInterviewsData]
  );

  const pieDataCOS = useMemo(
    () => buildPieData(cosData),
    [cosData]
  );

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setDetailsContext(null);
  };

  const handleCellClick = (row, col) => {
    if (!col.section || !row?.rawType) return;
    setDetailsContext({
      section: col.section,
      status: col.status || null,
      type: row.rawType,
      filialId: selectedCampus !== "all" ? selectedCampus : null,
      startDate: startDate || null,
      endDate: endDate || null,
      label: `${row.type} - ${col.label}`,
    });
    setDetailsOpen(true);
  };

  return (
    <div className="h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-hidden">
      <PageHeader>
        <Breadcrumbs currentPage={currentPage} />
        <FiltersBar />
      </PageHeader>

      <div className="flex-1 w-full overflow-y-auto pt-6">
        <div className="w-full pb-6 flex flex-col gap-8">
          <div className="flex flex-wrap gap-8">
            <div className={`${CARD_CLASS} flex-1 p-6`} style={{ minWidth: 320 }}>
              <h3 className="text-sm font-semibold text-gray-700 mb-6">Total Visa Interviews</h3>
              
              <div className="flex flex-wrap items-center gap-6">
                <div style={{ width: 96, height: 96 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieDataVisa}
                        cx="50%"
                        cy="50%"
                        innerRadius={28}
                        outerRadius={44}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieDataVisa.map((entry) => (
                          <Cell
                            key={entry.name}
                            fill={
                              entry.name === "Approved"
                                ? "#22c55e"
                                : entry.name === "Denied"
                                ? "#ef4444"
                                : entry.name === "Processing"
                                ? "#f59e0b"
                                : "#94a3b8"
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                  <SmallStatCard
                    label="Total"
                    value={formatNumber(visaInterviewsData?.total ?? 0)}
                  />
                  <SmallStatCard
                    label="Approved"
                    value={formatNumber(visaInterviewsData?.approved ?? 0)}
                    color="text-green-600"
                  />
                  <SmallStatCard
                    label="Denied"
                    value={formatNumber(visaInterviewsData?.denied ?? 0)}
                    color="text-red-500"
                  />
                  <SmallStatCard
                    label="Processing"
                    value={formatNumber(visaInterviewsData?.processing ?? 0)}
                    color="text-amber-600"
                  />
                  <SmallStatCard
                    label="Approval Index"
                    value={formatPercent(visaInterviewsData?.approval_index ?? 0)}
                    color="text-gray-800"
                  />
                </div>
              </div>
            </div>

            <div className={`${CARD_CLASS} flex-1 p-6`} style={{ minWidth: 320 }}>
              <h3 className="text-sm font-semibold text-gray-700 mb-6">Total COS</h3>
              
              <div className="flex flex-wrap items-center gap-6">
                <div style={{ width: 96, height: 96 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieDataCOS}
                        cx="50%"
                        cy="50%"
                        innerRadius={28}
                        outerRadius={44}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieDataCOS.map((entry) => (
                          <Cell
                            key={entry.name}
                            fill={
                              entry.name === "Approved"
                                ? "#22c55e"
                                : entry.name === "Denied"
                                ? "#ef4444"
                                : entry.name === "Processing"
                                ? "#f59e0b"
                                : "#94a3b8"
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                  <SmallStatCard
                    label="Total"
                    value={formatNumber(cosData?.total ?? 0)}
                  />
                  <SmallStatCard
                    label="Approved"
                    value={formatNumber(cosData?.approved ?? 0)}
                    color="text-green-600"
                  />
                  <SmallStatCard
                    label="Denied"
                    value={formatNumber(cosData?.denied ?? 0)}
                    color="text-red-500"
                  />
                  <SmallStatCard
                    label="Processing"
                    value={formatNumber(cosData?.processing ?? 0)}
                    color="text-amber-600"
                  />
                  <SmallStatCard
                    label="Approval Index"
                    value={formatPercent(cosData?.approval_index ?? 0)}
                    color="text-gray-800"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={`${CARD_CLASS} p-6`}>
            <div className="flex flex-wrap items-center gap-6 mb-6 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Expected Start Date</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange"
                />
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Campus:</span>
                <select
                  value={selectedCampus}
                  onChange={(e) => setSelectedCampus(e.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange"
                >
                  <option value="all">All Campuses</option>
                  {filials.map((filial) => (
                    <option key={filial.id} value={String(filial.id)}>
                      {filial.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Agent</span>
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-mila_orange"
                >
                  <option value="all">All agents</option>
                  {agents.map((agent) => (
                    <option key={agent.agent_id} value={String(agent.agent_id)}>
                      {agent.agent_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th
                      className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50 border border-gray-200"
                      rowSpan={2}
                    ></th>
                    {columnGroups.map((group, idx) => (
                      <th
                        key={idx}
                        className="px-3 py-3 text-center font-semibold text-gray-700 bg-gray-50 border border-gray-200"
                        colSpan={group.columns.length}
                      >
                        {group.title}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {allColumns.map((col, idx) => (
                      <th
                        key={idx}
                        className="px-3 py-3 text-center font-medium text-gray-600 bg-gray-50 border border-gray-200 whitespace-nowrap"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, rowIndex) => (
                    <tr
                      key={row.type}
                      className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-4 py-3 font-medium text-gray-700 border border-gray-200">
                        {row.type}
                      </td>
                      {allColumns.map((col, colIdx) => {
                        const value = row[col.key];
                        const isClickable = Boolean(col.section && row.rawType);
                        return (
                          <td
                            key={colIdx}
                            className="px-3 py-3 text-center text-gray-600 border border-gray-200"
                          >
                            {value !== null ? (
                              <button
                                type="button"
                                onClick={() => handleCellClick(row, col)}
                                disabled={!isClickable}
                                className={`w-full text-center ${
                                  isClickable
                                    ? "hover:text-mila_orange hover:underline cursor-pointer"
                                    : "cursor-default"
                                }`}
                              >
                                {formatNumber(value)}
                              </button>
                            ) : (
                              ""
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <DSODashboardDetailsModal
        isOpen={detailsOpen}
        onClose={handleCloseDetails}
        context={detailsContext}
      />
    </div>
  );
}
